import React, { useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import HorizontalScrollContainer from './micro-components/HorizontalScrollContainer'
import BlogResourceItem from './micro-components/BlogResourceItem'
import VideoCard from './micro-components/VideoCard'
import blogsIcon from '@/assets/report/website.svg'
import videosIcon from '@/assets/report/videos.svg'

export type SuggestedResource = {
  type?: string
  title?: string
  link: string
  thumbnailUrl?: string
  _id?: string
}

interface SuggestedResourcesProps {
  resources: SuggestedResource[]
  status?: string
  isCheatedInterview?: boolean
  isSharedReport?: boolean
  onGenerateResources?: () => void | Promise<void>
  isResourcesGenerating?: boolean
}

const SuggestedResources: React.FC<SuggestedResourcesProps> = ({
  resources,
  status,
  isCheatedInterview,
  isSharedReport,
  onGenerateResources,
  isResourcesGenerating = false,
}) => {
  const { blogs, videos } = useMemo(() => {
    const grouped = { blogs: [] as SuggestedResource[], videos: [] as SuggestedResource[] }
    resources.forEach((resource) => {
      if (!resource?.link) return
      const type = resource.type?.toLowerCase()
      if (type === 'blog' || type === 'website') {
        grouped.blogs.push(resource)
      } else if (type === 'youtube' || type === 'video') {
        grouped.videos.push(resource)
      }
    })
    return grouped
  }, [resources])

  const hasResources = blogs.length > 0 || videos.length > 0
  const isGenerating = isResourcesGenerating || status === 'in_progress' || status === 'generating'
  const isReady = status === 'completed' || hasResources
  const shouldShowGenerateButton = Boolean(onGenerateResources) && !isReady && !isGenerating

  if (isCheatedInterview || isSharedReport) return null

  return (
    <section
      id="suggested-resources"
      className="scroll-mt-28 bg-white rounded-lg p-8 sm:p-4 overflow-x-hidden"
    >
      <div className="flex items-center justify-between mb-6 gap-4">
        <h2 className="text-sub-header font-bold text-gray-800 font-dm-sans">Suggested Resources</h2>

        {shouldShowGenerateButton && (
          <button
            onClick={onGenerateResources}
            disabled={isGenerating}
            className="flex items-center gap-2 px-4 py-2 bg-button hover:bg-button-hover text-white text-button rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              'Generate Resources'
            )}
          </button>
        )}
      </div>

      {isGenerating && !hasResources && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-green" />
          <span className="ml-3 text-gray-600">Generating resources...</span>
        </div>
      )}

      {hasResources && (
        <>
          {blogs.length > 0 && (
            <div className="flex flex-col gap-2 overflow-x-hidden">
              <div className="text-sm font-medium text-gray-800 flex items-center gap-2">
                <img src={blogsIcon} alt="blogs" className="w-6 h-6" />
                <p>Blogs</p>
              </div>
              <HorizontalScrollContainer>
                {blogs.map((resource, idx) => (
                  <BlogResourceItem
                    key={resource._id ?? `${resource.link}-${idx}`}
                    url={resource.link}
                    title={resource.title}
                  />
                ))}
              </HorizontalScrollContainer>
            </div>
          )}

          {videos.length > 0 && (
            <div className="flex flex-col gap-4 overflow-x-hidden mt-6">
              <div className="text-sm font-medium text-gray-800 flex items-center gap-2">
                <img src={videosIcon} alt="videos" className="w-6 h-6" />
                <p>Videos to watch</p>
              </div>
              <HorizontalScrollContainer className="gap-3 pr-1">
                {videos.map((resource, idx) => (
                  <VideoCard
                    key={resource._id ?? `${resource.link}-${idx}`}
                    video={{
                      title: resource.title || 'Watch video',
                      url: resource.link,
                    }}
                  />
                ))}
              </HorizontalScrollContainer>
            </div>
          )}
        </>
      )}

      {!hasResources && !isGenerating && (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <p className="text-gray-500">No resources available yet.</p>
        </div>
      )}
    </section>
  )
}

export default SuggestedResources

